import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { useQueryClient } from '@tanstack/react-query';
import { useRole } from '@/contexts/RoleContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Play, CheckCircle, Lock, Clock, Sparkles, FileText, Video, Headphones, ChevronLeft, ChevronRight, HelpCircle, Circle, PlayCircle, Layers, Filter, X, Star, Calendar, CalendarDays, Image as ImageIcon, MessageCircle, Search, RotateCcw, Info, User, Compass } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { contentOnboardingSteps, contentOnboardingBenefits } from '@/data/onboardingConfigs';
import { ContentPlayerModal } from '@/components/ContentPlayerModal';
import { CertificationHeader, ROUTES_TOOLTIP_TEXT } from '@/components/CertificationHeader';
import { useChaptersWithContent, useAllContentProgress, useUpdateContentProgress, useAllContentByCategory } from '@/hooks/useContent';
import type { ContentItem } from '@/hooks/useContent';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { SelfPacedRoutePanel } from '@/components/participant/SelfPacedRoutePanel';
import { useSelfPacedSnapshot } from '@/hooks/useSelfPacedRoutes';
import { resolveSnapshotLink, isAddedAfterEnrollment } from '@/lib/selfPacedSnapshot';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useLicensePrice } from '@/hooks/useLicensePrice';
import { isEnrollmentExpired, shouldOpenAllContent, contentAccessSource } from '@/lib/cohortAccess';
import { SubscriptionUpsellModal } from '@/components/SubscriptionUpsellModal';

type ContentStatus = 'completed' | 'in_progress' | 'not_started';
type ContentRequirement = 'mandatory' | 'optional';

interface ContentLesson {
  id: string;
  title: string;
  duration: string;
  type: 'video' | 'reading' | 'audio' | 'infographic' | 'interactive' | 'lia-class' | 'resource';
  completed: boolean;
  progress?: number;
  mandatory?: boolean;
  addedAfterEnrollment?: boolean;
  thumbnail: string;
  description: string;
  vimeo_player_url?: string | null;
  html_content?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
  author?: string | null;
  tags?: string[] | null;
  target_levels?: number[] | null;
  access_level?: string | null;
}

interface ContentChapter {
  id: string;
  title: string;
  description: string;
  lessons: ContentLesson[];
  unlocked: boolean;
  week?: number;
}

// Fallback thumbnails
const fallbackThumbnails = [
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=225&fit=crop',
];

// Map DB content_type to UI lesson type
const mapContentType = (dbType: string | null): ContentLesson['type'] => {
  switch (dbType) {
    case 'video':
    case 'Video': return 'video';
    case 'interactive':
    case 'Interactivo':
    case 'ova': return 'interactive';
    case 'reading':
    case 'Lectura':
    case 'article': return 'reading';
    case 'Recurso': return 'resource';
    case 'infographic':
    case 'Infografía':
    case 'audio':
    case 'Audio':
    case 'podcast': return 'infographic';
    case 'lia-class':
    case 'Clase con LIA': return 'lia-class';
    default: return 'reading';
  }
};

const levelColors = 'from-primary to-secondary';

function LessonCard({
  lesson,
  unlocked,
  index,
  onOpenLesson,
  onUncomplete,
  requiresSubscription = false,
}: {
  lesson: ContentLesson;
  unlocked: boolean;
  index: number;
  onOpenLesson: (lesson: ContentLesson, index: number) => void;
  onUncomplete?: (lessonId: string) => void;
  requiresSubscription?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getLessonIcon = (type: ContentLesson['type']) => {
    switch (type) {
      case 'video': return <Video className="w-3 h-3" />;
      case 'reading': return <FileText className="w-3 h-3" />;
      case 'resource': return <FileText className="w-3 h-3" />;
      case 'audio': return <Headphones className="w-3 h-3" />;
      case 'infographic': return <ImageIcon className="w-3 h-3" />;
      case 'lia-class': return <MessageCircle className="w-3 h-3" />;
      case 'interactive': return <Layers className="w-3 h-3" />;
    }
  };

  const getTypeLabel = (type: ContentLesson['type']) => {
    switch (type) {
      case 'video': return 'Video';
      case 'reading': return 'Lectura';
      case 'resource': return 'Recurso';
      case 'audio': return 'Audio';
      case 'infographic': return 'Infografía';
      case 'lia-class': return 'Clase con LIA';
      case 'interactive': return 'Interactivo';
    }
  };

  const handleClick = () => {
    if (unlocked || requiresSubscription) onOpenLesson(lesson, index);
  };

  return (
    <div
      className={cn(
        'group relative flex-shrink-0 w-[280px] cursor-pointer transition-all duration-500',
        !unlocked && !requiresSubscription && 'opacity-50 pointer-events-none'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div
        className={cn(
          'relative rounded-xl overflow-hidden transition-all duration-500 bg-card border border-border',
          isHovered && 'scale-105 z-20 shadow-2xl shadow-black/50 border-primary/50'
        )}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={lesson.thumbnail}
            alt={lesson.title}
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-all duration-700',
              isHovered && 'scale-110'
            )}
          />
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent transition-opacity duration-300',
              isHovered ? 'opacity-100' : 'opacity-60'
            )}
          />

          {/* Hover Action Button */}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-all duration-300',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
            <div
              className={cn(
                'w-14 h-14 rounded-full bg-white/90 flex items-center justify-center transition-transform duration-300',
                isHovered && 'scale-110'
              )}
            >
              {lesson.type === 'video' && <Play className="w-6 h-6 text-black fill-black ml-1" />}
              {lesson.type === 'reading' && <BookOpen className="w-6 h-6 text-black" />}
              {lesson.type === 'audio' && <Headphones className="w-6 h-6 text-black" />}
              {lesson.type === 'infographic' && <ImageIcon className="w-6 h-6 text-black" />}
              {lesson.type === 'lia-class' && <MessageCircle className="w-6 h-6 text-black" />}
              {lesson.type === 'interactive' && <Layers className="w-6 h-6 text-black" />}
              {lesson.type === 'resource' && <FileText className="w-6 h-6 text-black" />}
            </div>
          </div>

          {/* Duration Badge */}
          <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/70 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {lesson.duration}
          </div>

          {/* Type Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <div className="px-2 py-1 rounded bg-black/70 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1">
              {getLessonIcon(lesson.type)}
              {getTypeLabel(lesson.type)}
            </div>
            {lesson.mandatory && (
              <div className="px-2 py-1 rounded bg-secondary/90 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" />
              </div>
            )}
            {lesson.addedAfterEnrollment && (
              <div className="px-2 py-1 rounded bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-medium">
                Nuevo · opcional
              </div>
            )}
            {requiresSubscription && (
              <div className="px-2 py-1 rounded bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Pro
              </div>
            )}
          </div>

          {/* Completed Badge — clickable to uncomplete */}
          {lesson.completed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUncomplete?.(lesson.id);
              }}
              title="Desmarcar completado"
              className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-success/90 backdrop-blur-sm hover:bg-destructive/90 transition-colors group/uncomplete z-20"
            >
              <CheckCircle className="w-4 h-4 text-white group-hover/uncomplete:hidden" />
              <RotateCcw className="w-4 h-4 text-white hidden group-hover/uncomplete:block" />
              <span className="text-white text-xs font-medium group-hover/uncomplete:hidden">Completado</span>
              <span className="text-white text-xs font-medium hidden group-hover/uncomplete:block">Desmarcar</span>
            </button>
          )}

          {/* In Progress Badge */}
          {!lesson.completed && lesson.progress && lesson.progress > 0 && (
            <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-warning/90 backdrop-blur-sm">
              <PlayCircle className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-medium">{lesson.progress}%</span>
            </div>
          )}

          {/* Info Button */}
          {(lesson.description || lesson.author || (lesson.tags && lesson.tags.length > 0) || (lesson.target_levels && lesson.target_levels.length > 0)) && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="absolute bottom-3 left-3 w-7 h-7 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center text-white hover:bg-primary transition-colors z-20"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-80 text-sm"
                side="top"
                align="start"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground line-clamp-2">{lesson.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      {getLessonIcon(lesson.type)}
                      {getTypeLabel(lesson.type)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {lesson.duration}
                    </span>
                  </div>
                  {lesson.description && (
                    <p className="text-muted-foreground text-xs leading-relaxed">{lesson.description}</p>
                  )}
                  {lesson.author && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <User className="w-3.5 h-3.5" />
                      <span>{lesson.author}</span>
                    </div>
                  )}
                  {lesson.target_levels && lesson.target_levels.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Dirigido a</span>
                      <div className="flex flex-wrap gap-1">
                        {lesson.target_levels.sort((a, b) => a - b).map((lvl) => (
                          <Badge key={lvl} variant="outline" className="text-[10px] px-1.5 py-0">
                            Nivel {lvl}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {lesson.tags && lesson.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {lesson.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px] px-2 py-0">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Lock Icon */}
          {!unlocked && !requiresSubscription && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60">
              <Lock className="w-10 h-10 text-white/60" />
            </div>
          )}

          {/* Subscription overlay */}
          {requiresSubscription && (
            <div
              className={cn(
                'absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-[2px] gap-2 transition-opacity duration-300',
                isHovered ? 'opacity-100' : 'opacity-80'
              )}
            >
              <Sparkles className="w-8 h-8 text-primary" />
              <span className="text-white text-xs font-semibold px-3 text-center">
                Suscríbete para acceder
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h4 className="font-semibold text-foreground text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
            {lesson.title}
          </h4>
          <div
            className={cn(
              'overflow-hidden transition-all duration-500',
              isHovered ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'
            )}
          >
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {lesson.description}
            </p>
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 h-8 text-xs">
                {lesson.completed ? (
                  <><RotateCcw className="w-3 h-3 mr-1" />Volver a ver</>
                ) : lesson.progress > 0 ? (
                  <><Play className="w-3 h-3 mr-1" />Continuar</>
                ) : (
                  <><Play className="w-3 h-3 mr-1" />Iniciar</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {lesson.completed ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
        ) : lesson.progress && lesson.progress > 0 ? (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
            <div
              className="h-full bg-warning transition-all duration-300"
              style={{ width: `${lesson.progress}%` }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ChapterRow({
  chapter,
  index,
  onOpenLesson,
  onUncomplete,
  lockSubscription = false,
}: {
  chapter: ContentChapter;
  index: number;
  onOpenLesson: (lesson: ContentLesson, index: number) => void;
  onUncomplete: (lessonId: string) => void;
  lockSubscription?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -600 : 600,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      return () => ref.removeEventListener('scroll', checkScroll);
    }
  }, []);

  return (
    <div className="relative group/row mb-8">
      {/* Chapter Header */}
      <div className="flex items-center gap-3 mb-4 px-2">
        <div
          className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm',
            chapter.unlocked ? `bg-gradient-to-br ${levelColors}` : 'bg-muted'
          )}
        >
          {chapter.unlocked ? index + 1 : <Lock className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground text-lg">{chapter.title}</h3>
            {chapter.week && (
              <Badge
                variant="outline"
                className="bg-primary/10 text-primary border-primary/30 gap-1 text-xs"
              >
                <Calendar className="w-3 h-3" />
                Semana {chapter.week}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{chapter.description}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
          <span>{chapter.lessons.length} lecciones</span>
          {chapter.unlocked && (
            <span className="text-primary">
              • {chapter.lessons.filter((l) => l.completed).length} completadas
            </span>
          )}
        </div>
      </div>

      {/* Lessons Carousel */}
      <div className="relative">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card/95 border border-border shadow-xl flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all -translate-x-2 md:-translate-x-4"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        )}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card/95 border border-border shadow-xl flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all translate-x-2 md:translate-x-4"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        )}

        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-2 pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {chapter.lessons.map((lesson, lessonIndex) => (
            <LessonCard
              key={lesson.id}
              lesson={lesson}
              unlocked={chapter.unlocked}
              index={lessonIndex}
              onOpenLesson={onOpenLesson}
              onUncomplete={onUncomplete}
              requiresSubscription={lockSubscription && lesson.access_level === 'subscription'}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ContentPage() {
  const { currentUserId } = useRole();
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('content_onboarding_seen')
  );
  const [selectedLesson, setSelectedLesson] = useState<ContentLesson | null>(null);
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(0);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const { enrollments, isLoading: enrollLoading, selectedCohortId, selectedProgramId, activeEnrollment, setSelectedCohortId } = useSelectedCohort();
  const { hasPaidAccess, hasEnterpriseAccess, hasProAccess, isLoading: accessLoading } = useUserAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [explicitContentSelection, setExplicitContentSelection] = useState(() => searchParams.has('cohort'));
  const navigate = useNavigate();
  const [viewAllContentManual, setViewAllContentManual] = useState(false);
  const activeExpired = isEnrollmentExpired(activeEnrollment);
  const { data: selfPacedSnapshot } = useSelfPacedSnapshot(activeEnrollment?.cohort_id, !!activeEnrollment?.is_perpetual);
  const autoSwitchedRef = useRef(false);
  const autoOpen = !enrollLoading && !accessLoading && !!activeEnrollment && !autoSwitchedRef.current && shouldOpenAllContent({
    expired: activeExpired, paidAccess: hasPaidAccess, explicitSelection: explicitContentSelection || searchParams.has('cohort'),
    perpetual: activeEnrollment.is_perpetual, viewOnly: !!activeEnrollment.view_only,
  });

  // Derived: show "all content" view either by user choice OR when the user has no enrollments.
  // Computed (not via useEffect) so the very first render is correct and doesn't flash the empty state.
  const viewAllContent = viewAllContentManual || autoOpen || (!enrollLoading && enrollments.length === 0);
  const setViewAllContent = setViewAllContentManual;
  const { label: proPriceLabel, periodLabel: proPeriodLabel } = useLicensePrice();
  const expiredDateLabel = activeEnrollment?.content_access_deadline
    ? new Date(activeEnrollment.content_access_deadline).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const [showProFallbackNotice, setShowProFallbackNotice] = useState(false);
  const accessSource = contentAccessSource(hasEnterpriseAccess, hasProAccess);
  const continuedAccessMessage = accessSource === 'enterprise'
    ? 'Con el Plan Enterprise de tu organización sigues disfrutando de todo el contenido.'
    : accessSource === 'pro'
      ? 'Con tu Plan Pro sigues disfrutando de todo el contenido.'
      : 'Tu acceso vigente por otra cohorte te permite seguir viendo todo el contenido.';
  const handleContentCohortChange = (id: string) => {
    setExplicitContentSelection(true);
    setShowProFallbackNotice(false);
    setViewAllContent(false);
    setSelectedCohortId(id);
  };
  useEffect(() => {
    if (autoSwitchedRef.current || enrollLoading || accessLoading || searchParams.has('cohort')) return;
    if (!activeEnrollment) return;
    autoSwitchedRef.current = true;
    if (autoOpen) {
      setViewAllContentManual(true);
      setShowProFallbackNotice(true);
    }
  }, [enrollLoading, accessLoading, activeEnrollment, autoOpen, searchParams]);
  const [activeTypeFilter, setActiveTypeFilter] = useState<ContentLesson['type'] | 'all'>('all');
  const [activeStatusFilter, setActiveStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [activeRequirementFilter, setActiveRequirementFilter] = useState<ContentRequirement | 'all'>('all');
  const [activeWeekFilter, setActiveWeekFilter] = useState<number | 'all'>('all');
  const [activeLevelFilter, setActiveLevelFilter] = useState<number | 'all'>('all');
  const [activeAccessFilter, setActiveAccessFilter] = useState<'all' | 'free' | 'subscription'>('all');

  const [viewMode, setViewMode] = useState<'chapters' | 'timeline'>('chapters');
  const [searchQuery, setSearchQuery] = useState('');
  const [progressOverrides, setProgressOverrides] = useState<Record<string, { completed: boolean; progress_percent: number }>>({});
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  // Real data hooks
  const { data: chaptersData, isLoading: chaptersLoading } = useChaptersWithContent(viewAllContent ? undefined : selectedProgramId);
  const { data: allContentByCategory, isLoading: allContentLoading } = useAllContentByCategory(hasPaidAccess);
  const { data: progressRecords } = useAllContentProgress();
  const updateProgress = useUpdateContentProgress();

  // Build progress lookup
  const progressMap = useMemo(() => {
    const map = new Map<string, { completed: boolean; progress_percent: number }>();
    (progressRecords ?? []).forEach((p) => {
      map.set(p.content_id, {
        completed: p.completed ?? false,
        progress_percent: p.progress_percent ?? 0,
      });
    });
    Object.entries(progressOverrides).forEach(([id, val]) => {
      map.set(id, val);
    });
    return map;
  }, [progressRecords, progressOverrides]);

  // Transform DB chapters to UI chapters (program view)
  const programChapters: ContentChapter[] = useMemo(() => {
    if (viewAllContent || !chaptersData || chaptersData.length === 0) return [];

    const snap = activeEnrollment?.is_perpetual ? (selfPacedSnapshot ?? null) : null;
    const mapped = chaptersData.map((ch, chIndex) => {
      const lessons: ContentLesson[] = ch.contents.map((cc, ccIndex) => {
        const c = cc.content;
        const snapLink = resolveSnapshotLink(snap, c.id, !!cc.is_mandatory);
        const prog = progressMap.get(c.id);
        return {
          id: c.id,
          title: c.name,
          duration: c.duration ?? '10 min',
          type: mapContentType(c.content_type),
          completed: prog?.completed ?? false,
          progress: prog?.progress_percent ?? 0,
          mandatory: snapLink.mandatory,
          addedAfterEnrollment: isAddedAfterEnrollment(snap, c.id),
          thumbnail: c.thumbnail_url ?? fallbackThumbnails[(chIndex * 6 + ccIndex) % fallbackThumbnails.length],
          description: c.description ?? '',
          vimeo_player_url: c.vimeo_player_url ?? (c.url?.includes('player.vimeo.com') ? c.url : null),
          html_content: c.html_content,
          url: c.url,
          thumbnail_url: c.thumbnail_url,
          author: c.author,
          tags: c.tags,
          target_levels: c.target_levels,
        };
      });

      return {
        id: ch.id,
        title: ch.name,
        description: `${lessons.length} lecciones`,
        lessons,
        unlocked: true,
        week: ch.week_number ?? undefined,
      };
    });

    const enrollment = enrollments.find(e => e.program_id === selectedProgramId);
    const sequentialUnlock = enrollment?.sequential_unlock ?? false;

    if (sequentialUnlock) {
      for (let i = 0; i < mapped.length; i++) {
        if (i === 0) {
          mapped[i].unlocked = true;
          continue;
        }
        const prev = mapped[i - 1];
        const mandatoryLessons = prev.lessons.filter((l) => l.mandatory);
        if (mandatoryLessons.length === 0) {
          mapped[i].unlocked = true;
        } else {
          const completedMandatory = mandatoryLessons.filter((l) => l.completed).length;
          mapped[i].unlocked = completedMandatory / mandatoryLessons.length >= 0.8;
        }
      }
    }

    return mapped;
  }, [viewAllContent, chaptersData, progressMap, enrollments, selectedProgramId, selfPacedSnapshot, activeEnrollment?.is_perpetual]);

  // Transform category-based content to chapters (all content view)
  const categoryChapters: ContentChapter[] = useMemo(() => {
    if (!viewAllContent || !allContentByCategory) return [];

    return Object.entries(allContentByCategory).map(([category, items], catIndex) => {
      const lessons: ContentLesson[] = items.map((c: ContentItem, ccIndex: number) => {
        const prog = progressMap.get(c.id);
        return {
          id: c.id,
          title: c.name,
          duration: c.duration ?? '10 min',
          type: mapContentType(c.content_type),
          completed: prog?.completed ?? false,
          progress: prog?.progress_percent ?? 0,
          mandatory: false,
          thumbnail: c.thumbnail_url ?? fallbackThumbnails[(catIndex * 6 + ccIndex) % fallbackThumbnails.length],
          description: c.description ?? '',
          vimeo_player_url: c.vimeo_player_url ?? (c.url?.includes('player.vimeo.com') ? c.url : null),
          html_content: c.html_content,
          url: c.url,
          thumbnail_url: c.thumbnail_url,
          author: c.author,
          tags: c.tags,
          target_levels: c.target_levels,
          access_level: c.access_level,
        };
      });

      return {
        id: `cat-${category}`,
        title: category,
        description: `${lessons.length} lecciones`,
        lessons,
        unlocked: true,
      };
    });
  }, [viewAllContent, allContentByCategory, progressMap]);

  const chapters = viewAllContent ? categoryChapters : programChapters;

  // Helpers
  const getLessonStatus = (lesson: ContentLesson): ContentStatus => {
    if (lesson.completed) return 'completed';
    if (lesson.progress && lesson.progress > 0) return 'in_progress';
    return 'not_started';
  };

  const getLessonRequirement = (lesson: ContentLesson): ContentRequirement => {
    return lesson.mandatory ? 'mandatory' : 'optional';
  };

  const contentTypeFilters: { value: ContentLesson['type'] | 'all'; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todos los tipos', icon: <Filter className="w-4 h-4" /> },
    { value: 'video', label: 'Videos', icon: <Video className="w-4 h-4" /> },
    { value: 'interactive', label: 'Interactivos', icon: <Layers className="w-4 h-4" /> },
    { value: 'infographic', label: 'Infografías', icon: <ImageIcon className="w-4 h-4" /> },
    { value: 'lia-class', label: 'Clase con LIA', icon: <MessageCircle className="w-4 h-4" /> },
    { value: 'reading', label: 'Lecturas', icon: <FileText className="w-4 h-4" /> },
    { value: 'resource', label: 'Recursos', icon: <FileText className="w-4 h-4" /> },
    { value: 'audio', label: 'Audio', icon: <Headphones className="w-4 h-4" /> },
  ];

  const contentStatusFilters: { value: ContentStatus | 'all'; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todos', icon: <Circle className="w-4 h-4" /> },
    { value: 'completed', label: 'Completados', icon: <CheckCircle className="w-4 h-4" /> },
    { value: 'in_progress', label: 'En progreso', icon: <PlayCircle className="w-4 h-4" /> },
    { value: 'not_started', label: 'No iniciados', icon: <Circle className="w-4 h-4" /> },
  ];

  // Auto-open content or filter by week from query params
  useEffect(() => {
    let changed = false;

    // Select a specific cohort/route (e.g. coming from /rutas)
    const cohortParam = searchParams.get('cohort');
    if (cohortParam) {
      setExplicitContentSelection(true);
      setShowProFallbackNotice(false);
      setViewAllContent(false);
      setSelectedCohortId(cohortParam);
      searchParams.delete('cohort');
      changed = true;
    }

    // Filter by week
    const weekParam = searchParams.get('week');
    if (weekParam) {
      setActiveWeekFilter(Number(weekParam));
      searchParams.delete('week');
      changed = true;
    }

    // Filter by mandatory only
    const mandatoryParam = searchParams.get('mandatory');
    if (mandatoryParam === '1' || mandatoryParam === 'true') {
      setActiveRequirementFilter('mandatory');
      searchParams.delete('mandatory');
      changed = true;
    }

    // Filter by category (from project suggestions, etc.)
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setCategoryFilter(categoryParam);
      setViewAllContent(true);
      searchParams.delete('category');
      changed = true;
    }

    // Auto-open specific content — wait until access check is resolved
    // to avoid showing upsell to users who actually have paid access.
    const openId = searchParams.get('open');
    // A recommendation can be outside the currently selected route. Resolve
    // it in the catalog, which retains the normal subscription access gate.
    if (openId && !viewAllContent && !enrollLoading && !chaptersLoading && !accessLoading
      && !chapters.some(chapter => chapter.lessons.some(lesson => lesson.id === openId))) {
      setViewAllContent(true);
    }
    if (openId && chapters.length > 0 && !accessLoading) {
      for (const chapter of chapters) {
        const idx = chapter.lessons.findIndex(l => l.id === openId);
        if (idx >= 0) {
          const lesson = chapter.lessons[idx];
          if (viewAllContent && lesson.access_level === 'subscription' && !hasPaidAccess) {
            setUpsellOpen(true);
          } else {
            setSelectedLesson(lesson);
            setSelectedLessonIndex(idx);
            setIsPlayerOpen(true);
          }
          searchParams.delete('open');
          changed = true;
          break;
        }
      }
    }

    if (changed) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, chapters, accessLoading, hasPaidAccess, viewAllContent, enrollLoading, chaptersLoading]);

  const handleOnboardingComplete = () => {
    localStorage.setItem('content_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  const [upsellOpen, setUpsellOpen] = useState(false);

  const handleOpenLesson = (lesson: ContentLesson, index: number) => {
    if (viewAllContent && lesson.access_level === 'subscription' && !accessLoading && !hasPaidAccess) {
      setUpsellOpen(true);
      return;
    }
    setSelectedLesson(lesson);
    setSelectedLessonIndex(index);
    setIsPlayerOpen(true);
  };

  const queryClient = useQueryClient();

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setSelectedLesson(null);
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    // El widget de progreso del inicio y el progreso semanal salen de aquí.
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  };

  const handleProgressSaved = useCallback((progress: { contentId: string; completed: boolean; progressPercent: number }) => {
    setProgressOverrides((prev) => ({
      ...prev,
      [progress.contentId]: {
        completed: progress.completed,
        progress_percent: progress.progressPercent,
      },
    }));
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    // El widget de progreso del inicio y el progreso semanal salen de aquí.
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  }, [queryClient]);

  const handleLessonComplete = (lessonId: string) => {
    // Vista de mentor: solo lectura, no se registra avance propio.
    if (activeEnrollment?.view_only) return;
    setProgressOverrides((prev) => ({ ...prev, [lessonId]: { completed: true, progress_percent: 100 } }));
    updateProgress.mutate({ content_id: lessonId, completed: true, progress_percent: 100 });
  };

  const handleLessonUncomplete = (lessonId: string) => {
    if (activeEnrollment?.view_only) return;
    setProgressOverrides((prev) => ({ ...prev, [lessonId]: { completed: false, progress_percent: 0 } }));
    updateProgress.mutate({ content_id: lessonId, completed: false, progress_percent: 0 });
  };


  // Filtered chapters
  const filteredChapters = useMemo(() => {
    return chapters
      .map((chapter) => ({
        ...chapter,
        lessons: chapter.lessons.filter((lesson) => {
          const typeMatch = activeTypeFilter === 'all' || lesson.type === activeTypeFilter;
          const statusMatch = activeStatusFilter === 'all' || getLessonStatus(lesson) === activeStatusFilter;
          const requirementMatch =
            activeRequirementFilter === 'all' || getLessonRequirement(lesson) === activeRequirementFilter;
          const levelMatch =
            activeLevelFilter === 'all' || (lesson.target_levels && lesson.target_levels.includes(activeLevelFilter as number));
          const accessMatch =
            activeAccessFilter === 'all' ||
            (activeAccessFilter === 'subscription'
              ? lesson.access_level === 'subscription'
              : lesson.access_level !== 'subscription');
          const searchMatch =
            searchQuery.trim() === '' ||
            lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lesson.description.toLowerCase().includes(searchQuery.toLowerCase());
          return typeMatch && statusMatch && requirementMatch && levelMatch && accessMatch && searchMatch;
        }),
      }))
      .filter((chapter) => {
        const weekMatch = activeWeekFilter === 'all' || chapter.week === activeWeekFilter;
        const chapterSearchMatch =
          searchQuery.trim() === '' ||
          chapter.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          chapter.lessons.length > 0;
        const categoryMatch =
          !categoryFilter || chapter.title.toLowerCase().trim() === categoryFilter.toLowerCase().trim();
        return chapter.lessons.length > 0 && weekMatch && chapterSearchMatch && categoryMatch;
      });
  }, [chapters, activeTypeFilter, activeStatusFilter, activeRequirementFilter, activeWeekFilter, activeLevelFilter, activeAccessFilter, searchQuery, categoryFilter]);


  // Stats
  const totalLessons = chapters.reduce((acc, ch) => acc + ch.lessons.length, 0);
  const completedLessons = chapters.reduce((acc, ch) => acc + ch.lessons.filter((l) => l.completed).length, 0);
  const availableWeeks = [...new Set(chapters.map((ch) => ch.week).filter(Boolean))].sort(
    (a, b) => (a || 0) - (b || 0)
  );

  const weekCounts = chapters.reduce((acc, ch) => {
    if (ch.week) acc[ch.week] = (acc[ch.week] || 0) + ch.lessons.length;
    return acc;
  }, {} as Record<number, number>);

  const weekProgress = chapters.reduce(
    (acc, ch) => {
      if (ch.week) {
        if (!acc[ch.week])
          acc[ch.week] = { total: 0, completed: 0, mandatory: 0, mandatoryCompleted: 0, chapters: [] as ContentChapter[] };
        acc[ch.week].total += ch.lessons.length;
        acc[ch.week].completed += ch.lessons.filter((l) => l.completed).length;
        acc[ch.week].mandatory += ch.lessons.filter((l) => l.mandatory).length;
        acc[ch.week].mandatoryCompleted += ch.lessons.filter((l) => l.mandatory && l.completed).length;
        acc[ch.week].chapters.push(ch);
      }
      return acc;
    },
    {} as Record<number, { total: number; completed: number; mandatory: number; mandatoryCompleted: number; chapters: ContentChapter[] }>
  );

  const lessonCounts = chapters.reduce((acc, ch) => {
    ch.lessons.forEach((l) => {
      acc[l.type] = (acc[l.type] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const statusCounts = chapters.reduce((acc, ch) => {
    ch.lessons.forEach((l) => {
      const s = getLessonStatus(l);
      acc[s] = (acc[s] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const requirementCounts = chapters.reduce((acc, ch) => {
    ch.lessons.forEach((l) => {
      const r = getLessonRequirement(l);
      acc[r] = (acc[r] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const isLoading = enrollLoading || accessLoading || (viewAllContent ? allContentLoading : chaptersLoading);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        onComplete={handleOnboardingComplete}
        steps={contentOnboardingSteps}
        benefits={contentOnboardingBenefits}
        ctaText="Explorar Contenido"
      />
    );
  }

  // Empty state when no chapters/items are available
  if (chapters.length === 0 && !(activeExpired && !viewAllContent && !activeEnrollment?.is_perpetual && !activeEnrollment?.view_only)) {
    return (
      <div className="space-y-6">
        <CertificationHeader
          enrollments={enrollments}
          selectedCohortId={selectedCohortId}
          onCohortChange={handleContentCohortChange}
          showAllContentOption
          onSelectAllContent={() => setViewAllContent(true)}
          isAllContentSelected={viewAllContent}
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-foreground mb-2">
            {viewAllContent ? 'Biblioteca vacía' : 'No hay contenido disponible'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {viewAllContent
              ? 'Aún no se ha publicado contenido en la biblioteca. Vuelve pronto.'
              : 'Aún no se han publicado capítulos para tu ruta. Vuelve pronto.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Help Button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-20 right-4 z-50 text-muted-foreground hover:text-foreground bg-card/50 backdrop-blur-sm"
        onClick={() => setShowOnboarding(true)}
        title="Ver ayuda"
      >
        <HelpCircle className="w-5 h-5" />
      </Button>

      {/* Certification Header */}
      <CertificationHeader
        enrollments={enrollments}
        selectedCohortId={selectedCohortId}
        onCohortChange={handleContentCohortChange}
        showAllContentOption
        onSelectAllContent={() => setViewAllContent(true)}
        isAllContentSelected={viewAllContent}
      />

      {/* Vista de mentor: la cohorte se ve solo para revisión */}
      {!viewAllContent && activeEnrollment?.view_only && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Vista de mentor (solo lectura).</span>{' '}
          Estás revisando el contenido de esta cohorte. No se registra avance ni racha a tu nombre.
        </div>
      )}

      {/* Banda compacta de rutas cuando se ve todo el contenido */}
      {!enrollLoading && enrollments.length === 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Compass className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">¿Quieres un camino guiado?</span>{' '}
              Únete a una ruta de aprendizaje y avanza con un plan estructurado.
            </p>
          </div>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" className="sm:ml-auto flex-shrink-0 gap-1.5" onClick={() => navigate('/rutas')}>
                  <Compass className="w-4 h-4" />
                  Explorar rutas
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                {ROUTES_TOOLTIP_TEXT}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Panel de ruta de autoaprendizaje */}
      {!viewAllContent && activeEnrollment?.is_perpetual && chapters.length > 0 && (
        <SelfPacedRoutePanel
          cohortId={activeEnrollment.cohort_id}
          planStartAt={activeEnrollment.plan_start_at}
          chapters={chapters}
        />
      )}

      {/* Content Access Expired Check */}
      {viewAllContent && (showProFallbackNotice || autoOpen) && activeExpired && hasPaidAccess && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            El acceso a tu cohorte {activeEnrollment?.cohort_name} finalizó el {expiredDateLabel}. {continuedAccessMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Content Access Expired Check */}
      {!viewAllContent && activeExpired && !activeEnrollment?.is_perpetual && !activeEnrollment?.view_only ? (
        hasPaidAccess ? (
          <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <h3 className="text-xl font-semibold text-foreground">El acceso a esta cohorte venció el {expiredDateLabel}</h3>
            <p className="text-muted-foreground max-w-md">{continuedAccessMessage}</p>
            <Button onClick={() => setViewAllContent(true)}>Ver todo el contenido</Button>
          </div>
        ) : (
          <Card className="glass-card max-w-xl mx-auto my-10 overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />
            <CardContent className="p-6 md:p-8 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Sigue aprendiendo con el Plan Pro</h3>
              <p className="text-muted-foreground">
                Tu acceso al contenido de la cohorte venció el <span className="font-medium text-foreground">{expiredDateLabel}</span>. Tu historial y progreso siguen guardados.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1.5 text-left max-w-sm mx-auto">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />Todo el contenido de la plataforma</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />Rutas de autoaprendizaje</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />Asistente LIA completo</li>
              </ul>
              <p className="text-2xl font-bold text-foreground">{proPriceLabel}<span className="text-sm font-normal text-muted-foreground">{proPeriodLabel}</span></p>
              <Button onClick={() => navigate('/planes')} className="w-full sm:w-auto">Adquirir Plan Pro</Button>
              <div>
                <button type="button" onClick={() => setViewAllContent(true)} className="text-sm text-primary hover:underline">
                  o explora el contenido gratuito
                </button>
              </div>
            </CardContent>
          </Card>
        )
      ) : (
      <>
      {/* Filter Bar */}
      <div className="space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar contenido por título o descripción..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-10 bg-card border-border rounded-full"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status + Mandatory Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {contentStatusFilters.map((filter) => {
            const count = filter.value === 'all' ? totalLessons : statusCounts[filter.value] || 0;
            if (filter.value !== 'all' && count === 0) return null;
            const statusColors: Record<string, string> = {
              completed: 'bg-success text-white',
              in_progress: 'bg-warning text-white',
              not_started: 'bg-muted text-muted-foreground',
            };
            return (
              <button
                key={filter.value}
                onClick={() => setActiveStatusFilter(filter.value)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap',
                  activeStatusFilter === filter.value
                    ? filter.value === 'all'
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : `${statusColors[filter.value]} shadow-md`
                    : 'bg-card hover:bg-muted text-muted-foreground border border-border'
                )}
              >
                {filter.icon}
                {filter.label}
                <span
                  className={cn(
                    'px-1.5 py-0.5 rounded-full text-xs',
                    activeStatusFilter === filter.value
                      ? 'bg-white/20 text-inherit'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}

          {!viewAllContent && (
            <>
              <div className="h-6 w-px bg-border mx-1" />

              <label className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-muted transition-colors">
                <Checkbox
                  checked={activeRequirementFilter === 'mandatory'}
                  onCheckedChange={(checked) =>
                    setActiveRequirementFilter(checked ? 'mandatory' : 'all')
                  }
                  className="h-4 w-4"
                />
                <Star className="w-4 h-4 text-secondary" />
                <span className="text-foreground">Solo obligatorio</span>
                <span className="text-xs text-muted-foreground">
                  ({requirementCounts['mandatory'] || 0})
                </span>
              </label>

              <div className="h-6 w-px bg-border mx-1" />

              <Select
                value={String(activeWeekFilter)}
                onValueChange={(value) =>
                  setActiveWeekFilter(value === 'all' ? 'all' : Number(value))
                }
              >
                <SelectTrigger className="w-auto min-w-[160px] h-9 rounded-full border-border bg-card text-sm font-medium">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Todas las semanas" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span>Todas las semanas</span>
                      <span className="text-xs text-muted-foreground">({totalLessons})</span>
                    </div>
                  </SelectItem>
                  {availableWeeks.map((week) => (
                    <SelectItem key={week} value={String(week)}>
                      <div className="flex items-center gap-2">
                        <span>Semana {week}</span>
                        <span className="text-xs text-muted-foreground">
                          ({weekCounts[week!] || 0})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          <Select
            value={activeTypeFilter}
            onValueChange={(value) =>
              setActiveTypeFilter(value as ContentLesson['type'] | 'all')
            }
          >
            <SelectTrigger className="w-auto min-w-[180px] h-9 rounded-full border-border bg-card text-sm font-medium">
              <Filter className="w-4 h-4 mr-2 shrink-0" />
              <SelectValue placeholder="Tipo de contenido" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border z-50">
              {contentTypeFilters.map((filter) => {
                const count =
                  filter.value === 'all' ? totalLessons : lessonCounts[filter.value] || 0;
                if (filter.value !== 'all' && count === 0) return null;
                return (
                  <SelectItem key={filter.value} value={filter.value}>
                    <div className="flex items-center gap-2">
                      <span>{filter.label}</span>
                      <span className="text-xs text-muted-foreground">({count})</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Select
            value={activeLevelFilter === 'all' ? 'all' : String(activeLevelFilter)}
            onValueChange={(value) =>
              setActiveLevelFilter(value === 'all' ? 'all' : Number(value))
            }
          >
            <SelectTrigger className="w-auto min-w-[180px] h-9 rounded-full border-border bg-card text-sm font-medium">
              <Sparkles className="w-4 h-4 mr-2 shrink-0" />
              <SelectValue placeholder="Dirigido a nivel" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border z-50">
              <SelectItem value="all">Todos los niveles</SelectItem>
              {[0, 1, 2, 3, 4, 5].map((lvl) => (
                <SelectItem key={lvl} value={String(lvl)}>
                  Nivel {lvl}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {viewAllContent && !accessLoading && !hasPaidAccess && (
            <Select
              value={activeAccessFilter}
              onValueChange={(value) =>
                setActiveAccessFilter(value as 'all' | 'free' | 'subscription')
              }
            >
              <SelectTrigger className="w-auto min-w-[180px] h-9 rounded-full border-border bg-card text-sm font-medium">
                <Lock className="w-4 h-4 mr-2 shrink-0" />
                <SelectValue placeholder="Nivel de acceso" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border z-50">
                <SelectItem value="all">Todo el contenido</SelectItem>
                <SelectItem value="free">Solo gratuito</SelectItem>
                <SelectItem value="subscription">Solo Pro</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>


        {/* Clear + View Mode */}
        <div className="flex flex-wrap items-center gap-2">

          {categoryFilter && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/30">
              <Filter className="w-3 h-3" />
              Categoría: {categoryFilter}
              <button
                onClick={() => setCategoryFilter(null)}
                className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                aria-label="Quitar filtro de categoría"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}


          {(activeTypeFilter !== 'all' ||
            activeStatusFilter !== 'all' ||
            activeRequirementFilter !== 'all' ||
            activeWeekFilter !== 'all' ||
            activeAccessFilter !== 'all' ||
            activeLevelFilter !== 'all') && (
            <button
              onClick={() => {
                setActiveTypeFilter('all');
                setActiveStatusFilter('all');
                setActiveRequirementFilter('all');
                setActiveWeekFilter('all');
                setActiveLevelFilter('all');
                setActiveAccessFilter('all');
              }}

              className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}

          {/* View Mode Toggle - only in program view */}
          {!viewAllContent && (
            <div className="ml-auto flex items-center gap-1 bg-muted rounded-full p-1">
              <button
                onClick={() => setViewMode('chapters')}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                  viewMode === 'chapters'
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Layers className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                  viewMode === 'timeline'
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <CalendarDays className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {(viewAllContent || viewMode === 'chapters') ? (
        <div className="px-6 space-y-6">
          {filteredChapters.length > 0 ? (
            filteredChapters.map((chapter, index) => (
              <ChapterRow
                key={chapter.id}
                chapter={chapter}
                index={index}
                onOpenLesson={handleOpenLesson}
                onUncomplete={handleLessonUncomplete}
                lockSubscription={viewAllContent && !accessLoading && !hasPaidAccess}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Filter className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">
                No hay contenido con estos filtros
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                No se encontraron lecciones con los filtros seleccionados.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setActiveTypeFilter('all');
                  setActiveStatusFilter('all');
                  setActiveRequirementFilter('all');
                  setActiveWeekFilter('all');
                }}
              >
                Ver todo el contenido
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Timeline View */
        <div className="px-6">
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-accent" />
            <div className="space-y-8">
              {availableWeeks.map((week) => {
                const progress = weekProgress[week!];
                if (!progress) return null;

                const weekPercentage =
                  progress.total > 0
                    ? Math.round((progress.completed / progress.total) * 100)
                    : 0;
                const mandatoryPercentage =
                  progress.mandatory > 0
                    ? Math.round(
                        (progress.mandatoryCompleted / progress.mandatory) * 100
                      )
                    : 0;
                const isCompleted = weekPercentage === 100;
                const isCurrentWeek = weekPercentage > 0 && weekPercentage < 100;

                return (
                  <div key={week} className="relative pl-16">
                    <div
                      className={cn(
                        'absolute left-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-4 transition-all',
                        isCompleted
                          ? 'bg-success border-success text-white'
                          : isCurrentWeek
                          ? 'bg-primary border-primary text-white animate-pulse'
                          : 'bg-card border-border text-muted-foreground'
                      )}
                    >
                      {isCompleted ? <CheckCircle className="w-6 h-6" /> : week}
                    </div>

                    <div
                      className={cn(
                        'bg-card border rounded-xl p-5 transition-all hover:shadow-lg',
                        isCurrentWeek ? 'border-primary shadow-md' : 'border-border'
                      )}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-lg text-foreground">
                              Semana {week}
                            </h3>
                            {isCurrentWeek && (
                              <Badge className="bg-primary text-primary-foreground text-xs">
                                Actual
                              </Badge>
                            )}
                            {isCompleted && (
                              <Badge className="bg-success text-white text-xs">
                                Completada
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {progress.chapters.length} capítulo
                            {progress.chapters.length !== 1 ? 's' : ''} •{' '}
                            {progress.total} lecciones
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-foreground">
                            {weekPercentage}%
                          </div>
                          <p className="text-xs text-muted-foreground">completado</p>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground">Progreso total</span>
                            <span className="font-medium">
                              {progress.completed}/{progress.total}
                            </span>
                          </div>
                          <Progress value={weekPercentage} className="h-2" />
                        </div>
                        {progress.mandatory > 0 && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-muted-foreground flex items-center gap-1">
                                <Star className="w-3 h-3 text-secondary" />
                                Obligatorios
                              </span>
                              <span className="font-medium">
                                {progress.mandatoryCompleted}/{progress.mandatory}
                              </span>
                            </div>
                            <Progress value={mandatoryPercentage} variant="accent" className="h-2" />
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        {progress.chapters.map((chapter) => (
                          <button
                            key={chapter.id}
                            onClick={() => {
                              setActiveWeekFilter(week!);
                              setViewMode('chapters');
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left group"
                          >
                            <div
                              className={cn(
                                'w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium',
                                chapter.unlocked
                                  ? 'bg-gradient-to-br ' + levelColors
                                  : 'bg-muted-foreground'
                              )}
                            >
                              {chapter.unlocked ? (
                                <BookOpen className="w-4 h-4" />
                              ) : (
                                <Lock className="w-4 h-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-foreground truncate group-hover:text-primary transition-colors">
                                {chapter.title}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {chapter.lessons.filter((l) => l.completed).length}/
                                {chapter.lessons.length} completadas
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content Player Modal */}
      <ContentPlayerModal
        isOpen={isPlayerOpen}
        onClose={handleClosePlayer}
        lesson={selectedLesson}
        lessonIndex={selectedLessonIndex}
        onComplete={handleLessonComplete}
        onProgressSaved={handleProgressSaved}
      />

      <SubscriptionUpsellModal open={upsellOpen} onOpenChange={setUpsellOpen} />
    </>
      )}
    </div>
  );
}
